import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  Button,
  IconButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

function BookingsTable({ bookings, onStatusChange, onMarkPaid, onDelete, onNoShowCharge, onApprove, onReject }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const formatDateNice = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (dateStr === today.toISOString().split('T')[0]) return 'Today';
    if (dateStr === tomorrow.toISOString().split('T')[0]) return 'Tomorrow';

    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'success',
      pending: 'warning',
      cancelled: 'error',
      completed: 'info',
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      completed: 'success',
      pending: 'warning',
      failed: 'error',
    };
    return colors[status] || 'warning';
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'booking_date',
        header: 'When',
        cell: ({ getValue, row }) => (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {formatDateNice(getValue())}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getValue()}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: 'start_time',
        header: 'Time',
        cell: ({ getValue }) => (
          <Typography variant="body2" fontWeight={500}>
            {getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'customer_name',
        header: 'Customer',
        cell: ({ row }) => (
          <Box>
            <Link
              to={`/admin/customer/${row.original.customer_id}`}
              style={{
                color: theme.palette.secondary.main,
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              {row.original.customer_name}
            </Link>
            <Typography variant="caption" display="block" color="text.secondary">
              {row.original.customer_phone}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: 'service_name',
        header: 'Service',
        cell: ({ getValue }) => (
          <Typography variant="body2">{getValue()}</Typography>
        ),
      },
      {
        accessorKey: 'service_price',
        header: 'Price',
        cell: ({ getValue, row }) => (
          <Box>
            {row.original.discount_amount > 0 ? (
              <>
                <Typography variant="body2" fontWeight={500} color="success.main">
                  £{(getValue() - row.original.discount_amount).toFixed(2)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textDecoration: 'line-through' }}
                >
                  £{getValue()}
                </Typography>
                <Typography variant="caption" display="block" color="secondary.main">
                  {row.original.discount_code}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" fontWeight={500}>
                £{getValue()}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        accessorKey: 'payment_status',
        header: 'Payment',
        cell: ({ getValue, row }) => (
          <Box>
            <Chip
              label={getValue() || 'pending'}
              color={getPaymentStatusColor(getValue())}
              size="small"
              sx={{ fontWeight: 500 }}
            />
            {row.original.stripe_payment_method_id && (
              <Typography variant="caption" display="block" color="secondary.main" sx={{ mt: 0.5 }}>
                Card saved
              </Typography>
            )}
          </Box>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue, row }) => (
          <Select
            value={getValue()}
            onChange={(e) => onStatusChange(row.original.id, e.target.value)}
            size="small"
            variant="outlined"
            sx={{
              minWidth: 120,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${getStatusColor(getValue())}.main`,
              },
              color: `${getStatusColor(getValue())}.main`,
              fontWeight: 500,
            }}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {/* Approve/Reject buttons for pending bookings */}
            {row.original.status === 'pending' && onApprove && (
              <Button
                variant="contained"
                size="small"
                color="success"
                onClick={() => onApprove(row.original.id)}
                startIcon={<CheckCircleIcon />}
                sx={{ minWidth: 90 }}
              >
                Approve
              </Button>
            )}
            {row.original.status === 'pending' && onReject && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => onReject(row.original)}
                startIcon={<CancelIcon />}
                sx={{ minWidth: 80 }}
              >
                Reject
              </Button>
            )}
            {/* Mark Paid button - only for confirmed bookings with pending payment */}
            {row.original.status !== 'pending' && row.original.payment_status !== 'completed' && (
              <Button
                variant="outlined"
                size="small"
                color="success"
                onClick={() => onMarkPaid(row.original.id)}
                sx={{ minWidth: 80 }}
              >
                Mark Paid
              </Button>
            )}
            {row.original.stripe_payment_method_id && !row.original.marked_noshow && row.original.status !== 'pending' && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => onNoShowCharge(row.original)}
                sx={{ minWidth: 80 }}
              >
                No-Show
              </Button>
            )}
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(row.original.id)}
              aria-label="delete"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [theme, onStatusChange, onMarkPaid, onNoShowCharge, onDelete, onApprove, onReject]
  );

  const table = useReactTable({
    data: bookings,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (bookings.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          bgcolor: 'info.light',
          borderRadius: 2,
          color: 'info.dark',
        }}
      >
        <Typography variant="body1">No bookings found</Typography>
      </Box>
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {bookings.map((booking) => (
          <Paper
            key={booking.id}
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                elevation: 4,
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.12), 0 4px 8px -2px rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {formatDateNice(booking.booking_date)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {booking.booking_date} • {booking.start_time}
                </Typography>
              </Box>
              <Chip
                label={booking.status}
                color={getStatusColor(booking.status)}
                size="small"
                sx={{ fontWeight: 500 }}
              />
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Customer
              </Typography>
              <Link
                to={`/admin/customer/${booking.customer_id}`}
                style={{
                  color: theme.palette.secondary.main,
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              >
                {booking.customer_name}
              </Link>
              <Typography variant="caption" display="block" color="text.secondary">
                {booking.customer_phone}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Service
                </Typography>
                <Typography variant="body2">{booking.service_name}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Price
                </Typography>
                {booking.discount_amount > 0 ? (
                  <>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      £{(booking.service_price - booking.discount_amount).toFixed(2)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textDecoration: 'line-through' }}
                    >
                      £{booking.service_price}
                    </Typography>
                    <Typography variant="caption" display="block" color="secondary.main">
                      {booking.discount_code}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" fontWeight={600}>
                    £{booking.service_price}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Payment Status
              </Typography>
              <Chip
                label={booking.payment_status || 'pending'}
                color={getPaymentStatusColor(booking.payment_status)}
                size="small"
                sx={{ fontWeight: 500 }}
              />
              {booking.stripe_payment_method_id && (
                <Typography variant="caption" display="block" color="secondary.main" sx={{ mt: 0.5 }}>
                  Card saved
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              {/* Approve/Reject buttons for pending bookings */}
              {booking.status === 'pending' && onApprove && (
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  onClick={() => onApprove(booking.id)}
                  fullWidth
                  startIcon={<CheckCircleIcon />}
                >
                  Approve Booking
                </Button>
              )}
              {booking.status === 'pending' && onReject && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => onReject(booking)}
                  fullWidth
                  startIcon={<CancelIcon />}
                >
                  Reject Booking
                </Button>
              )}

              {/* Status dropdown - only show for non-pending bookings */}
              {booking.status !== 'pending' && (
                <Select
                  value={booking.status}
                  onChange={(e) => onStatusChange(booking.id, e.target.value)}
                  size="small"
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              )}

              {/* Mark Paid button - only for confirmed bookings with pending payment */}
              {booking.status !== 'pending' && booking.payment_status !== 'completed' && (
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  onClick={() => onMarkPaid(booking.id)}
                  fullWidth
                >
                  Mark as Paid
                </Button>
              )}
              {booking.stripe_payment_method_id && !booking.marked_noshow && booking.status !== 'pending' && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => onNoShowCharge(booking)}
                  fullWidth
                >
                  Charge No-Show
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => onDelete(booking.id)}
                fullWidth
                startIcon={<DeleteIcon />}
              >
                Delete Booking
              </Button>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  // Desktop table view
  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell key={header.id} sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} hover>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default BookingsTable;
